/**
 * AI bağlantı anahtarı — üst barın sağ üstünde açık/kapalı.
 *
 * Kapsam kuralı: AI yalnızca ANAHTARIN AÇILDIĞI PROFİLİN verisini görür.
 * Anahtar profil başına saklanır (profiles.ai_enabled); bir profilin açması
 * diğerini açmaz, bir hesabın açması başka hesabı hiç etkilemez.
 *
 * Varsayılan KAPALI. Mali kayıtların tamamının analiz edilmesi açık rıza
 * gerektirir; açık varsayılan rızayı varsaymak olurdu.
 *
 * KALDIRMAK İÇİN: bu klasörü sil, shell/index.ts listesinden satırı çıkar,
 * index.html'deki #ai-switch-wrap ve #ai-consent-ovl kaplarını kaldır.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';
import { TEXTS } from './texts.ts';

let enabled = false;

function el(id: string): HTMLElement | null {
  return document.getElementById(id);
}

/* ── Anahtarın çizimi ──────────────────────────────────────────── */

function render(): void {
  const wrap = el('ai-switch-wrap');
  if (!wrap) return;
  const user = legacy.user;
  if (!user) { wrap.style.display = 'none'; return; }
  wrap.style.display = '';

  const on = enabled === true;
  const track = on ? 'var(--red)' : 'var(--c3)';
  const knob = on ? 'calc(100% - 15px)' : '2px';
  const label = on ? 'var(--red)' : 'var(--t2)';
  const esc = legacy.escapeHtml;

  wrap.innerHTML =
    '<button id="ai-switch" onclick="aiConsentToggle()" aria-pressed="' + (on ? 'true' : 'false') + '"'
    + ' title="' + esc(legacy.t(on ? 'ai_on' : 'ai_off')) + '"'
    + ' style="background:none;border:none;cursor:pointer;display:flex;align-items:center;gap:6px;padding:5px 7px;border-radius:9px">'
    + '<span style="font-size:11px;font-weight:700;letter-spacing:.5px;color:' + label + '">' + esc(legacy.t('ai_sw')) + '</span>'
    + '<span style="position:relative;width:32px;height:17px;border-radius:10px;background:' + track
    + ';border:1px solid var(--div);transition:background .2s;display:inline-block;flex-shrink:0">'
    + '<span style="position:absolute;top:1px;left:' + knob
    + ';width:13px;height:13px;border-radius:50%;background:#fff;transition:left .2s"></span>'
    + '</span></button>';
}

/* ── Açma / kapama ─────────────────────────────────────────────── */

/** Sunucuya yaz. Yazma başarısızsa anahtarı geri al — kullanıcı kapalı olan
 *  bir şeyi açık sanmasın. */
function save(on: boolean): void {
  const sb = legacy.sb;
  const user = legacy.user;
  if (!sb || !user || !user.sbProfileId) return;
  sb.from('profiles')
    .update({ ai_enabled: on, ai_enabled_at: new Date().toISOString() })
    .eq('id', user.sbProfileId)
    .then((r: { error?: unknown } | null) => {
      if (r && r.error) {
        enabled = !on;
        const u = legacy.user;
        if (u) u.aiEnabled = !on;
        render();
        try { legacy.toast(legacy.t('ai_save_fail')); } catch { /* toast yoksa sessiz */ }
      }
    });
}

/** Anahtar çevrildi: önce durumu yaz, sonra ne olduğunu anlat. */
function toggle(): void {
  const user = legacy.user;
  if (!user) return;
  const next = !enabled;
  enabled = next;
  user.aiEnabled = next;
  render();
  save(next);
  info(next);
}

/** Giriş sonrası profilden oku. */
function load(): void {
  const user = legacy.user;
  enabled = !!(user && user.aiEnabled);
  render();
}

/* ── Bilgilendirme penceresi ───────────────────────────────────── */

function info(on: boolean): void {
  const ovl = el('ai-consent-ovl');
  if (!ovl) return;
  const esc = legacy.escapeHtml;
  const points = on ? ['ai_on_1', 'ai_on_2', 'ai_on_3', 'ai_on_4']
                    : ['ai_off_1', 'ai_off_2', 'ai_off_3'];
  const head = on ? legacy.t('ai_safe_h') : legacy.t('ai_off_h');

  ovl.innerHTML =
    '<div class="mod" style="max-width:440px">'
    + '<div style="text-align:center;margin-bottom:14px">'
    + '<div style="font-size:34px;margin-bottom:8px">' + (on ? '🔒' : '⏸️') + '</div>'
    + '<div style="font-size:17px;font-weight:800;line-height:1.35">' + esc(head) + '</div>'
    + '</div>'
    + '<div style="margin-bottom:16px">'
    + points.map((k) =>
        '<div class="tip"><span style="color:var(--grn);flex-shrink:0">✓</span><span>'
        + esc(legacy.t(k)) + '</span></div>').join('')
    + '</div>'
    + '<button class="btn br bfu" onclick="aiConsentClose()">' + esc(legacy.t('ai_got')) + '</button>'
    + '</div>';
  ovl.classList.add('on');
}

function close(): void {
  const ovl = el('ai-consent-ovl');
  if (ovl) ovl.classList.remove('on');
}

export default defineFeature({
  id: 'ai-consent',
  label: 'AI bağlantı anahtarı',
  view: null,

  boot: () => { legacy.addTranslations(TEXTS); },

  legacyGlobals: {
    aiConsentToggle: () => toggle(),
    aiConsentLoad: () => load(),
    aiConsentClose: () => close(),
    aiConsentRender: () => render(),
    aiConsentEnabled: () => enabled === true,
  },

  owns: { tables: ['profiles.ai_enabled'] },
});
